'use client';

import React, { useEffect, useMemo } from 'react';

import { css, cva } from '@pt-group-fe/styled-system/css';
import {
  Box,
  Container,
  DesktopContainer,
  HStack,
  MobileContainer
} from '@pt-group-fe/styled-system/jsx';
import { center, srOnly } from '@pt-group-fe/styled-system/patterns';
import Link from 'next/link';
import { useMeasure, useWindowSize } from 'react-use';

import { LanguageNavigation, LanguageNavigationProps } from '@lib/components/languageNavigation';
import { MainNavigation } from '@lib/components/mainNavigation';
import { MobileNavigation } from '@lib/components/mobileNavigation';
// import { Search } from '@lib/components/search';
import { i18n, LANGUAGES_FALLBACK } from '@lib/services/i18n';
import { useTranslation } from '@lib/services/i18n/client';
import { PageData, PageProps } from '@lib/services/page';
import { Button } from '@lib/ui/button';
import { CmsLink } from '@lib/ui/cmsLink';
import { Img } from '@lib/ui/img';

const container = css({
  bg: 'white',
  pos: 'sticky',
  top: 0,
  zIndex: 3000
});

const content = css({
  p: { base: 4, xl: 6 },
  h: '92px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: { md: 4 }
});

const restHidden = cva({
  base: {
    visibility: 'visible'
  },
  variants: {
    isRestHidden: {
      true: { visibility: 'hidden' } // Hide the rest section when it is not needed
    }
  }
});

const logoLink = center({ flexShrink: 0 });

interface HeaderProps {
  rootPath: string;
  cms?: PageData['cms'];
  sitemap?: PageData['sitemap'];
  sitemapItem?: PageData['sitemapItem'];
  tenantUid: string;
  pageProps: PageProps;
  searchEnabled: boolean;
}
const Header: React.FC<HeaderProps> = ({
  searchEnabled,
  pageProps,
  sitemap,
  cms,
  tenantUid,
  sitemapItem,
  rootPath
}) => {
  //  const { rootPath, cms, sitemap, sitemapItem, tenantUid /* config */ } = pageData;
  const { t } = useTranslation();
  const rootItem = sitemap?.children?.find(item => item.pageId === 'home');
  const items = rootItem?.children?.filter(item => item.active && item.include.includes('header'));
  const logo = cms?.tenant?.logo;
  const languages = i18n.getLanguages(tenantUid);
  const [leftRef, leftMeasures] = useMeasure<HTMLDivElement>();
  const [rightRef, rightMeasures] = useMeasure<HTMLDivElement>();
  const windowSize = useWindowSize();

  const rest = useMemo(() => {
    return windowSize.width - leftMeasures.width - rightMeasures.width - 90; // 124 is the padding
  }, [leftMeasures.width, rightMeasures.width, windowSize.width]);

  const isRestHidden = rest <= 0 || leftMeasures.width === 0 || rightMeasures.width === 0;

  const [isEnforcedMobileNav, setEnforcedMobileNav] = React.useState({
    w: 1,
    val: rest < 0
  });

  const langNavProps: LanguageNavigationProps = {
    languages: languages,
    current: pageProps.params.lng,
    alternateLangs: sitemapItem?._alternateLangs,
    fallback: LANGUAGES_FALLBACK,
    position: 'header'
  };

  if (cms?.isBlogPost && cms.blogPost?._alternateLangs) {
    langNavProps.alternateLangs = cms.blogPost._alternateLangs as any;
  }

  useEffect(() => {
    if (rest > 0) {
      if (isEnforcedMobileNav.w === windowSize.width) return;
      if (rest > 0 && windowSize.width < isEnforcedMobileNav.w) return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setEnforcedMobileNav({ w: windowSize.width, val: rest < 0 });
  }, [isEnforcedMobileNav.w, rest, windowSize.width]);

  if (pageProps.params.slugs[0] === '_dev' && process.env.APP_ENV !== 'prod') {
    return null;
  }

  return (
    <header className={container}>
      <Container fullWidth noPadding>
        <div className={content}>
          <HStack pos="relative" h="full" ref={leftRef}>
            <Link className={logoLink} href={rootPath}>
              <Box
                id="logo-box"
                h={{ base: 11, lg: 12 }}
                display="flex"
                alignItems="center"
                pos="relative"
              >
                <Img
                  data={logo}
                  className={css({
                    width: 'auto!',
                    maxH: 12,
                    maxW: { base: 200, sm: 280, md: 310, lg: 330 }
                  })}
                  alt={cms?.tenant?.siteMeta?.title || 'Logo'}
                  width={330}
                  height={48}
                />
              </Box>
              <span className={srOnly()}>{t('header.backToHome')}</span>
            </Link>
          </HStack>
          <HStack
            data-right-ref
            pos="relative"
            h="full"
            justifyContent="flex-end"
            gap={{ base: 4 }}
            ref={rightRef}
          >
            <DesktopContainer className={restHidden({ isRestHidden })}>
              {!isEnforcedMobileNav.val && (
                <MainNavigation
                  sitemap={sitemap}
                  sitemapItem={sitemapItem}
                  flyoutMenus={cms?.flyoutMenus}
                  hasHeaderBanner={cms?.hasHeaderBanner}
                  hasSearch={searchEnabled}
                />
              )}
            </DesktopContainer>
            {/* SEARCH TEMPORARY DISABLED TILL FIXED */}
            {/* <DesktopContainer className={restHidden({ isRestHidden })}>
              <Search pageData={pageData} pageProps={pageProps} />
            </DesktopContainer>
            <MobileContainer>
              <Search pageData={pageData} pageProps={pageProps} />
            </MobileContainer> */}
            <DesktopContainer className={restHidden({ isRestHidden })}>
              {!isEnforcedMobileNav.val && (
                <LanguageNavigation {...langNavProps} position="header" />
              )}
            </DesktopContainer>
            <MobileContainer>
              {items && items?.length > 0 && (
                <MobileNavigation
                  langNavProps={{ ...langNavProps, position: 'mobile' }}
                  hasHeaderBanner={cms?.hasHeaderBanner}
                  flyoutMenus={cms?.flyoutMenus}
                  items={items}
                  activeItemId={sitemapItem?.id}
                  footerLinks={cms?.tenant?.footerLinks}
                />
              )}
            </MobileContainer>
            <DesktopContainer className={restHidden({ isRestHidden })}>
              {!isEnforcedMobileNav.val &&
                cms?.tenant?.footerLinks &&
                cms?.tenant?.footerLinks.length > 0 && (
                  <HStack gap={4}>
                    {cms.tenant?.footerLinks.map(l => (
                      <CmsLink key={l.id} link={l}>
                        <Button size="small" icon="arrow-right" variant={l.buttonType}>
                          {l.title}
                        </Button>
                      </CmsLink>
                    ))}
                  </HStack>
                )}
            </DesktopContainer>
          </HStack>

          {isEnforcedMobileNav.val && (
            <DesktopContainer className={restHidden({ isRestHidden })}>
              <MobileNavigation
                langNavProps={{ ...langNavProps, position: 'mobile' }}
                hasHeaderBanner={cms?.hasHeaderBanner}
                flyoutMenus={cms?.flyoutMenus}
                items={items}
                activeItemId={sitemapItem?.id}
                footerLinks={cms?.tenant?.footerLinks}
              />
            </DesktopContainer>
          )}
        </div>
      </Container>
    </header>
  );
};

export default Header;
